import { defineStore, storeToRefs } from 'pinia'
import { isValid } from 'date-fns'
import { computed, ref, watch } from 'vue'
import {
  type CategoryEvent,
  CategoryEventApi,
  type CreateEventDto,
  type Event,
  EventApi,
  type EventProjection,
  type UpdateEventDto,
} from '@/api-swagger'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useUserStore } from '@/stores/useUserStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useSuiteStore } from '@/stores/useSuiteStore'
import {
  areDatesOnSameDay,
  formatDate,
  formatDateRangeToPopoverText,
  normalizeEventInstantIso,
  parseUtcDate,
} from '@/utils/dateUtils'
import { colors } from '@/utils/colors'
import { withApiState } from '@/utils/withApiState'
import type { Context } from '@/utils/types'

interface CalendarAttribute {
  highlight?: {
    color: string
    fillMode: string
  }
  dot?: {
    color: string
    fillMode: string
  }
  dates:
    | {
        start: Date
        end: Date
      }
    | Date
  popover?: {
    label: string
  }
  order?: number
}

export const useEventStore = defineStore('event', () => {
  const userStore = useUserStore()
  const myTeamStore = useMyTeamStore()
  const partnerStore = usePartnerStore()
  const suiteStore = useSuiteStore()
  const { users, isManager } = storeToRefs(userStore)
  const eventsCharts = ref<EventProjection[]>([])
  const selectedEvent = ref<Event>()
  const eventsUser = ref<Event[]>([])
  const eventsPartner = ref<Event[]>([])
  const eventsSubordinates = ref<Event[]>([])
  const eventsFromCurrentMonth = ref<Event[]>([])
  const filters = ref<{ selectedCommercials: string[] }>({
    selectedCommercials: [] as string[],
  })
  const loadingEventsUser = ref(false)
  const loadingDiary = ref(false)
  const visibleDateRange = ref<{ startDate?: string; endDate?: string }>({})
  const userConnected = computed(() => userStore.userConnected)
  const partner = computed(() => partnerStore.partner)

  const eventsFilter = computed(() => {
    const res: { [key: string]: number[] } = {
      R1P: [],
      R2P: [],
      'R+P': [],
      R1C: [],
      R2C: [],
      'R+C': [],
    }

    for (const resKey in res) {
      for (let i = 0; i < 12; i++) {
        const count = eventsCharts.value.filter((event) => {
          if (event.categIds && event.categIds.length === 1) {
            const eventDate = new Date(event.start)
            return eventDate.getMonth() === i && event.categIds[0].name === resKey
          }
        })
        res[resKey].push(count.length)
      }
    }
    return res
  })
  const trendLines = computed(() => {
    const currentMonth = new Date().getMonth()
    const result: { [key: string]: any } = {
      RP: null,
      RC: null,
    }

    for (const stackType of ['RP', 'RC']) {
      if (currentMonth > 0) {
        const stackTotals = []
        for (let i = 0; i < 12; i++) {
          let total = 0
          if (stackType === 'RP') {
            total =
              eventsFilter.value['R1P'][i] +
              eventsFilter.value['R2P'][i] +
              eventsFilter.value['R+P'][i]
          } else {
            total =
              eventsFilter.value['R1C'][i] +
              eventsFilter.value['R2C'][i] +
              eventsFilter.value['R+C'][i]
          }
          stackTotals.push(total)
        }

        const dataPoints = stackTotals.slice(0, currentMonth)

        if (dataPoints.length >= 2) {
          const trendData = calculateRegressionLine(dataPoints, currentMonth)

          result[stackType] = {
            name: `Tendance ${stackType}`,
            type: 'line',
            color: stackType === 'RP' ? '#2b5876' : '#72d7c7',
            data: trendData,
            lineStyle: {
              type: 'dashed',
              width: 2,
              color: stackType === 'RP' ? '#2b5876' : '#72d7c7',
            },

            symbol: 'none',
            connectNulls: false,
          }
        }
      }
    }

    return result
  })

  function calculateRegressionLine(dataPoints: number[], currentMonth: number) {
    const n = dataPoints.length
    const sumX = dataPoints.reduce((sum, _, index) => sum + index, 0)
    const sumY = dataPoints.reduce((sum, value) => sum + value, 0)
    const sumXY = dataPoints.reduce((sum, value, index) => sum + index * value, 0)
    const sumXX = dataPoints.reduce((sum, _, index) => sum + index * index, 0)

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
    const intercept = (sumY - slope * sumX) / n

    const trendData = []
    for (let i = 0; i < 12; i++) {
      if (i < currentMonth) {
        trendData.push(slope * i + intercept)
      } else {
        trendData.push(null)
      }
    }
    return trendData
  }

  const chartSeries = computed(() => {
    const series = [
      {
        name: 'R1P',
        color: '#2b5876',
        type: 'bar',
        stack: 'RP',
        emphasis: {
          focus: 'series',
        },
        data: eventsFilter.value['R1P'],
      },
      {
        name: 'R2P',
        color: '#5580a5',
        type: 'bar',
        stack: 'RP',
        emphasis: {
          focus: 'series',
        },
        data: eventsFilter.value['R2P'],
      },
      {
        name: 'R+P',
        color: '#7ea3c0',
        type: 'bar',
        stack: 'RP',
        emphasis: {
          focus: 'series',
        },
        data: eventsFilter.value['R+P'],
      },
      {
        name: 'R1C',
        color: '#72d7c7',
        type: 'bar',
        stack: 'RC',
        emphasis: {
          focus: 'series',
        },
        data: eventsFilter.value['R1C'],
      },
      {
        name: 'R2C',
        color: '#68ada2',
        type: 'bar',
        stack: 'RC',
        emphasis: {
          focus: 'series',
        },
        data: eventsFilter.value['R2C'],
      },
      {
        name: 'R+C',
        color: '#4a8877',
        type: 'bar',
        stack: 'RC',
        emphasis: {
          focus: 'series',
        },
        data: eventsFilter.value['R+C'],
      },
    ]

    // Ajouter les lignes de tendance si elles existent
    if (trendLines.value.RP) {
      series.push(trendLines.value.RP)
    }
    if (trendLines.value.RC) {
      series.push(trendLines.value.RC)
    }

    return series
  })

  const calendarAttributes = computed(() => {
    const sortedEvents = [...eventsFromCurrentMonth.value]
      .filter(
        (e) =>
          Boolean(e.start && e.stop) &&
          isValid(new Date(e.start)) &&
          isValid(new Date(e.stop)),
      )
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())

    const eventsByDate = sortedEvents.reduce(
      (acc, event) => {
        const dateKey = new Date(event.start).toDateString()
        if (!acc[dateKey]) {
          acc[dateKey] = []
        }
        acc[dateKey].push(event)
        return acc
      },
      {} as Record<string, Event[]>,
    )

    const attributes: CalendarAttribute[] = Object.entries(eventsByDate).flatMap(
      ([, events]) => {
        const hiddenEventsCount = events.length - (events.length <= 4 ? 4 : 3)
        const visibleEvents = events.slice(0, 4)

        return visibleEvents.map((event, index) => {
          if (index === 3 && hiddenEventsCount > 0) {
            return {
              dot: {
                color: 'darkgrey',
                fillMode: 'light',
              },
              dates: {
                start: new Date(event.start),
                end: new Date(event.stop),
              },
              popover: {
                label: `+${hiddenEventsCount} rendez-vous supplémentaire${hiddenEventsCount > 1 ? 's' : ''}`,
              },
            }
          } else {
            return {
              dot: {
                color: 'darkgrey',
                fillMode: 'light',
              },
              dates: {
                start: new Date(event.start),
                end: new Date(event.stop),
              },
              popover: {
                label: event.name + ' ' + formatDateRangeToPopoverText(event.start, event.stop),
              },
            }
          }
        })
      },
    )

    attributes.push({
      highlight: {
        color: 'darkgrey',
        fillMode: 'light',
      },
      dates: new Date(),
    })

    return attributes.reverse()
  })

  const eventsPartnerList = computed(() => {
    return eventsPartner.value
      .map((event: Event) => {
        const isSameDate = areDatesOnSameDay(event.start, event.stop)

        return {
          name: event.name,
          start: isSameDate
            ? formatDate(event.start) + ' ' + formatDateRangeToPopoverText(event.start, event.stop)
            : formatDateRangeToPopoverText(event.start, event.stop),
          category: event.categ?.name || 'Non catégorisé',
          author: userStore.getLocalInfoUserById(event.userId)?.name || 'Inconnu',
        }
      })

      .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())
  })

  const eventsUserList = computed(() => {
    const list = isManager ? eventsSubordinates.value : eventsUser.value
    return list
      .map((event: Event) => {
        const isSameDate = formatDate(event.start) === formatDate(event.stop)

        return {
          ...event,
          name: event.name,
          start: isSameDate
            ? formatDate(event.start) + ' ' + formatDateRangeToPopoverText(event.start, event.stop)
            : formatDateRangeToPopoverText(event.start, event.stop),
          category: categories.value.find((c) => c.id === event.categIds)?.name || 'Non catégorisé',
          author: users.value?.find((u) => u.id === event.userId)?.name || 'Inconnu',
        }
      })
      .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())
  })

  const resourcesFullcalendar = computed(() => {
    let result: { id: string; title: string; eventColor?: string }[] = []
    if (userConnected.value) {
      let charCode = 96
      if (isManager) {
        // Résoudre le nom d'un commercial par email via useMyTeamStore (source unique boostcore)
        function resolveCommercialName(email: string): string {
          const member = myTeamStore.findByEmail(email)
          if (member) return myTeamStore.memberLabel(member)
          return 'Inconnu'
        }

        if (filters.value.selectedCommercials.length > 0) {
          result = filters.value.selectedCommercials.map((commercialEmail, index) => {
            charCode++
            return {
              id: String.fromCharCode(charCode),
              title: resolveCommercialName(commercialEmail),
              eventColor: colors[index],
            }
          })
        } else {
          // Par défaut seulement moi
          charCode++
          result = [{
            id: String.fromCharCode(charCode),
            title: userConnected.value?.name || 'Inconnu',
            eventColor: colors[0],
          }]
        }
      } else {
        charCode++
        result = [
          {
            id: String.fromCharCode(charCode),
            title: userConnected.value.name,
            eventColor: colors[0],
          },
        ]
      }
    }
    return result
  })

  // Helper function to map an event to FullCalendar format
  function mapEventToFullCalendarFormat(
    event: Event,
    resourceId: string,
    commercialEmail?: string,
  ) {
    // Use the provided commercialEmail or fallback to event.commercialEmail (which might be undefined)
    const email = commercialEmail || event.commercialEmail || ''

    return {
      ...event,
      id: event.id.toString(),
      title: event.name,
      description: event.description,
      start: parseUtcDate(event.start, 'yyyy-MM-dd HH:mm:ss'),
      end: parseUtcDate(event.stop, 'yyyy-MM-dd HH:mm:ss'),
      time: event.duration,
      resourceIds: [resourceId],
      commercialEmail: email,
      extendedProps: {
        commercialEmail: email,
      },
    }
  }

  function isMyEvent(event: Event) {
    return userConnected.value?.id === event.userId
  }

  // Helper function to filter events by commercial
  function filterEventsByCommercial(events: Event[], commercialEmail: string) {
    return events.filter((event: Event) => {
      if (
        commercialEmail &&
        event.partnerIds?.some((partner) => partner.email === commercialEmail)
      ) {
        return true
      }
      return (
        event.commercialEmail === commercialEmail ||
        event.partnerIds?.some((partner) => partner.email === commercialEmail)
      )
    })
  }

  // Helper function to add events to the collection, merging duplicates
  function addEventsToCollection(collection: any[], newEvents: any[]) {
    for (const newEvent of newEvents) {
      const existingEventIndex = collection.findIndex((e) => e.id === newEvent.id)

      if (existingEventIndex === -1) {
        collection.push(newEvent)
      } else {
        // Merge resourceIds for duplicate events
        const existingResourceIds = collection[existingEventIndex].resourceIds || []
        const newResourceIds = newEvent.resourceIds || []
        collection[existingEventIndex].resourceIds = [...existingResourceIds, ...newResourceIds]
      }
    }
    return collection
  }

  // Function to check if an event was created by the current user
  const isEventCreatedByUser = (event: Event): boolean => {
    if (!event) {
      return false
    }

    if (!userStore.getLocalInfoByPartnerEmail(event.commercialEmail)) return true
    return event.userId == userStore.getLocalInfoByPartnerEmail(event.commercialEmail)?.id
  }

  const eventsFullcalendar = computed(() => {
    let events: any[] = []

    if (isManager) {
      let charCode = 96

      // Par défaut seulement moi, le filtre commercial permet d'ajouter d'autres users
      const commercialsToProcess =
        filters.value.selectedCommercials.length > 0
          ? filters.value.selectedCommercials
          : [userConnected.value?.email].filter(Boolean)

      // Process each commercial
      commercialsToProcess.forEach((commercial) => {
        charCode++
        const resourceId = String.fromCharCode(charCode)

        // Filter and map events for this commercial
        const filtered = filterEventsByCommercial(eventsSubordinates.value, commercial || '')
        const sourceEvents = filtered ?? eventsUser.value
        const commercialEvents =
          sourceEvents.map((event) =>
            mapEventToFullCalendarFormat(event, resourceId, commercial || ''),
          ) || []

        // Add to collection, handling duplicates
        events = addEventsToCollection(events, commercialEvents)
      })
    } else {
      // For non-manager roles, use eventsSubordinates (populated by getEventsSubordinatesId)
      events = eventsSubordinates.value.map((event) =>
        mapEventToFullCalendarFormat(event, 'a', userConnected.value?.email || ''),
      )
    }

    return events
  })

  const categories = ref<CategoryEvent[]>([])
  const loading = ref(false)
  const loadingCategory = ref(false)
  const api = new EventApi()

  const getEventById = async (eventId: number) => {
    await withApiState(
      async () => {
        const event = (await api.getEventById({ id: eventId })) || []

        selectedEvent.value = {
          ...event,
          start: parseUtcDate(event.start, 'yyyy-MM-dd HH:mm:ss'),
          stop: parseUtcDate(event.stop, 'yyyy-MM-dd HH:mm:ss'),
        }
      },
      { loading },
    )
  }

  const getEventsByPartnerId = async (partnerId: number) => {
    await withApiState(
      async () => {
        let events: Event[]
        const suiteResponse = await suiteStore.suiteFetch(`event/partner/${partnerId}`, { method: 'GET' })
        if (suiteResponse && suiteResponse.ok) {
          events = (await suiteResponse.json()) || []
        } else {
          events = (await api.getEventsByPartnerId({ partnerId: partnerId })) || []
        }

        eventsPartner.value = events.map((event: Event) => {
          event.start = parseUtcDate(event.start, 'yyyy-MM-dd HH:mm:ss')
          event.stop = parseUtcDate(event.stop, 'yyyy-MM-dd HH:mm:ss')

          return {
            ...event,
          }
        })
      },
      { loading },
    )
  }

  const getMyEvents = async (startDate?: string, endDate?: string) => {
    startDate = startDate ?? visibleDateRange.value.startDate
    endDate = endDate ?? visibleDateRange.value.endDate
    await withApiState(
      async () => {
        const boostCoreStore = useBoostCorePermissionStore()
        const email = userConnected.value?.email || boostCoreStore.user?.email
        if (!email) return

        const suiteResponse = await suiteStore.suiteFetch('event/by-emails', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emails: [email], startDate, endDate }),
        })
        if (suiteResponse && suiteResponse.ok) {
          eventsUser.value = (await suiteResponse.json()) || []
        } else {
          eventsUser.value =
            (await api.getEventsByUserEmails({ userEmailsDto: { emails: [email] } })) || []
        }
      },
      { loading: loadingDiary },
    )
  }

  const getEventsByUserEmailAndCurrentMonth = async () => {
    loading.value = true
    loadingEventsUser.value = true
    try {
      const bcStore = useBoostCorePermissionStore()
      const currentEmail = bcStore.user?.email
      if (currentEmail) {
        const events =
          (await api.getEventsByUserEmailAndCurrentMonth({
            userEmail: currentEmail,
          })) || []

        eventsFromCurrentMonth.value = events
          .map((event) => ({
            ...event,
            start: normalizeEventInstantIso(event.start),
            stop: normalizeEventInstantIso(event.stop),
          }))
          .filter((e) => e.start && e.stop)
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
      loadingEventsUser.value = false
    }
  }

  const getEventsCharts = async (year?: number) => {
    await withApiState(async () => {
      if (!userConnected.value) {
        const bcStore2 = useBoostCorePermissionStore()
        const email = bcStore2.user?.email
        if (email) await userStore.getUserByEmail(email)
      }

      if (userConnected.value && userConnected.value.partnerId) {
        const myEmail = userConnected.value.email || ''
        const myPartnerId = userConnected.value.partnerId.toString()

        let emails: string[] = myEmail ? [myEmail] : []
        let ids: string[] = [myPartnerId]

        if (isManager) {
          // Source unique : useMyTeamStore (boostcore) — incluant le manager
          emails = myTeamStore.emails.filter((e) => !!e)
          ids = myTeamStore.partnerIds.map((p) => p.toString())
        }

        // Filter out empty emails
        emails = emails.filter((email) => email !== '')

        if (emails.length > 0 && ids.length > 0) {
          eventsCharts.value = (await api.getEventsCategoryByUsersIds({ ids, year })) || []
        } else {
          eventsCharts.value = []
        }
      }
    })
  }

  const getEventsSubordinatesId = async (startDate?: string, endDate?: string) => {
    if (startDate || endDate) visibleDateRange.value = { startDate, endDate }
    startDate = startDate ?? visibleDateRange.value.startDate
    endDate = endDate ?? visibleDateRange.value.endDate
    await withApiState(
      async () => {
        const bcStore3 = useBoostCorePermissionStore()
        const myEmail = userConnected.value?.email || bcStore3.user?.email
        if (!myEmail) return

        if (true) {
          let emails: string[] = [myEmail]

          if (isManager && filters.value && filters.value.selectedCommercials) {
            if (filters.value.selectedCommercials.length > 0) {
              // Source unique boostcore : on filtre les emails de l'équipe selon la sélection
              const teamEmails = new Set(myTeamStore.emails)
              emails = filters.value.selectedCommercials.filter((email) => teamEmails.has(email))
            } else {
              // Par défaut : seulement moi
              emails = [myEmail]
            }
          }

          // Filter out empty emails
          emails = emails.filter((email) => email !== '')

          if (emails.length > 0) {
            const suiteResponse = await suiteStore.suiteFetch('event/by-emails', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ emails, startDate, endDate }),
            })
            if (suiteResponse && suiteResponse.ok) {
              eventsSubordinates.value = (await suiteResponse.json()) || []
            } else {
              eventsSubordinates.value =
                (await api.getEventsByUserEmails({ userEmailsDto: { emails: emails } })) || []
            }
          } else {
            eventsSubordinates.value = []
          }
        }
      },
      { loading: loadingDiary },
    )
  }

  const createEvent = async (event: CreateEventDto, context: Context) => {
    loading.value = true
    try {
      let response: any
      const suiteResponse = await suiteStore.suiteFetch('event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      })
      if (suiteResponse && suiteResponse.ok) {
        response = await suiteResponse.json()
      } else {
        response = await api.createEvent({ createEventDto: event })
      }
      if (response) {
        if (context === 'partner') getEventsByPartnerId(partner.value?.id ?? 0)

        getEventsSubordinatesId(visibleDateRange.value.startDate, visibleDateRange.value.endDate)
        getMyEvents(visibleDateRange.value.startDate, visibleDateRange.value.endDate)
        getEventsCharts()
        getEventsByUserEmailAndCurrentMonth()
        return response
      }
    } catch (err) {
      throw new Error("Erreur API lors de la création de l'evenement.")
    } finally {
      loading.value = false
    }
  }

  const updateEvent = async (
    meeting: {
      id: number
      partnerIds: Array<number>
    },
    event: UpdateEventDto,
    partnerIds: Array<number>,
    context: Context,
  ) => {
    loading.value = true
    const partners: { push: number[]; pop: number[] } = {
      push: partnerIds,
      pop: [],
    }

    // si l'id du partner dans meetind.partnerIds n'est pas dans partnerIds on l'ajout dans partnerIds dans la propriété pop
    for (const partner of meeting.partnerIds) {
      if (!partners.push.includes(partner)) {
        partners.pop.push(partner)
      }
    }
    event.partnerIds = partners
    await api
      .updateEvent({ id: meeting.id, updateEventDto: event })
      .then(() => {
        if (context === 'partner') getEventsByPartnerId(partner.value?.id ?? 0)

        getEventsSubordinatesId(visibleDateRange.value.startDate, visibleDateRange.value.endDate)
        getMyEvents(visibleDateRange.value.startDate, visibleDateRange.value.endDate)
        getEventsCharts()
        getEventsByUserEmailAndCurrentMonth()
      })
      .catch(() => {
        throw new Error('Une erreur est survenue lors de la modification du rendez-vous')
      })
      .finally(() => (loading.value = false))
  }

  const deleteById = async (eventId: number) => {
    loading.value = true
    try {
      await api.deleteEvent({ id: eventId })
    } catch (err) {
      console.error(err)
      throw new Error('Une erreur est survenue lors de la suppression du rendez-vous')
    }
  }

  const getAllCategoryEvent = async () => {
    loadingCategory.value = true
    const api = new CategoryEventApi()

    categories.value = await api
      .getAllCategoryEvent()
      .then((res) => res)
      .catch(() => {
        throw new Error('Une erreur est survenue lors de la recuperation des catégorie')
      })
      .finally(() => {
        loadingCategory.value = false
      })
  }

  watch(isManager, async () => {
    if (isManager) {
      if (userConnected.value?.email)
        filters.value.selectedCommercials = [userConnected.value.email]
    }
    await getEventsCharts()
  })

  watch(filters.value, async () => {
    await getEventsSubordinatesId(visibleDateRange.value.startDate, visibleDateRange.value.endDate)
  })

  return {
    filters,
    eventsUser,
    chartSeries,
    trendLines,
    eventsFromCurrentMonth,
    calendarAttributes,
    eventsPartner,
    eventsPartnerList,
    resourcesFullcalendar,
    eventsFullcalendar,
    getMyEvents,
    loading,
    loadingEventsUser,
    loadingDiary,
    eventsCharts,
    eventsSubordinates,
    eventsUserList,
    categories,
    getEventsByPartnerId: getEventsByPartnerId,
    createEvent: createEvent,
    updateEvent: updateEvent,
    getAllCategoryEvent: getAllCategoryEvent,
    getEventsCharts: getEventsCharts,
    getEventsSubordinatesId: getEventsSubordinatesId,
    getEventsByUserEmailAndCurrentMonth,
    getEventById,
    selectedEvent,
    isMyEvent,
    deleteById,
    isEventCreatedByUser,
  }
})
